import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { apiGet, apiPut } from "../../shared/apiClient";
import { useToast } from "../../shared/ToastContext";
import {
  ServiceEditorFormFields,
  defaultServiceForm
} from "./ServiceEditorFormFields";

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function getRowValue(row, key) {
  if (!row || !key) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, key)) return row[key];
  const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
  return row[key] ?? row[pascalKey];
}

export function EditServicePage() {
  const { id: idParam } = useParams();
  const id = idParam && /^\d+$/.test(String(idParam)) ? Number(idParam) : NaN;
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);
  const [form, setForm] = useState({ ...defaultServiceForm });

  useEffect(() => {
    if (!isAdmin) return;
    void (async () => {
      const res = await apiGet("/api/Customers");
      if (res.ok) setCustomers(Array.isArray(res.data) ? res.data : []);
    })();
  }, [isAdmin]);

  useEffect(() => {
    if (!Number.isFinite(id)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await apiGet(`/api/Services/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        toast.push(String(res.error || "Failed to load service"), "error");
        return;
      }
      const row = res.data;
      setForm({
        id: row.id,
        customerId: String(getRowValue(row, "customerId") ?? user?.customerId ?? ""),
        code: getRowValue(row, "code") ?? "",
        name: getRowValue(row, "name") ?? "",
        subtitle: getRowValue(row, "subtitle") ?? "",
        shortDescription: getRowValue(row, "shortDescription") ?? "",
        description: getRowValue(row, "description") ?? "",
        content: getRowValue(row, "content") ?? "",
        detailContentMode: getRowValue(row, "detailContentMode") === "json" ? "json" : "html",
        sectionsJson: getRowValue(row, "sectionsJson") ?? "",
        image: getRowValue(row, "image") ?? "",
        order: Number(getRowValue(row, "order") ?? 0),
        isActive: getRowValue(row, "isActive") ?? getRowValue(row, "IsActive") ?? true
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [id, user?.customerId, toast]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!Number.isFinite(id)) return;

    const customerId = isAdmin
      ? Number(form.customerId || 0)
      : Number(user?.customerId || 0);
    if (!customerId) {
      toast.push("Customer is required", "error");
      return;
    }
    if (!String(form.name || "").trim()) {
      toast.push("Name is required", "error");
      return;
    }

    const payload = {
      id,
      customerId,
      code: String(form.code || "").trim() || null,
      name: String(form.name || "").trim(),
      subtitle: String(form.subtitle || "").trim() || null,
      shortDescription: String(form.shortDescription || "").trim() || null,
      description: String(form.description || "").trim() || null,
      content: form.content || null,
      detailContentMode: form.detailContentMode === "json" ? "json" : "html",
      sectionsJson: String(form.sectionsJson || "").trim() || null,
      image: String(form.image || "").trim() || null,
      order: toNumber(form.order, 0),
      isActive: !!form.isActive
    };

    setSaving(true);
    const res = await apiPut(`/api/Services/${id}`, payload);
    setSaving(false);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to update service"), "error");
      return;
    }
    toast.push("Service updated", "success");
    navigate("/services");
  };

  if (loading) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">Loading...</div>;
  }

  return (
    <div className="w-full space-y-4">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Edit service</h1>
            <p className="mt-1 text-sm text-slate-500">Update service details and landing page content.</p>
          </div>
          <div className="flex gap-2">
            <Link
              to={`/services/${id}/preview`}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Preview content
            </Link>
            <button
              type="button"
              onClick={() => navigate("/services")}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Back
            </button>
            <button
              type="submit"
              form="edit-service-form"
              disabled={saving}
              className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <form id="edit-service-form" onSubmit={onSubmit}>
          <ServiceEditorFormFields
            form={form}
            setForm={setForm}
            isAdmin={isAdmin}
            customers={customers}
            htmlMode={htmlMode}
            setHtmlMode={setHtmlMode}
          />
        </form>
      </div>
    </div>
  );
}
